import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectEvents } from '../../helpers/projectEvents';
import { createCopyLinkProjectFn } from '../../helpers/copyLink';
import { useToasts, DropdownListItem, MagicBoxRow } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import styled from 'styled-components';
import { useHistory, useRouteMatch } from "react-router-dom";
import { API, Designer, DesignerAPI, Endpoints, Project, ProjectAPI } from '@brg/api';
import { CompetitionsDropdown } from '../CompetitionsDropdown/CompetitionsDropdown';
import { ShowcasesDropdown } from '../ShowcasesDropdown/ShowcasesDropdown';
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { ProjectDelegate } from "../../../project-delegate";
import { AppTester } from "../../../supporting";
import { protectProcessStatuses } from 'shared/helpers/protectStatus';
import { css } from 'styled-components';
import { HsePolicies } from 'shared/constants/HsePolicies';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import { FoldersDropdown, ORIENTATION_TYPE } from '../FoldersDropdown/FoldersDropdown';
import { downloadPDF } from '../PrintProject/model/downloadPDF';
import { ProjectStatusDrafts } from 'shared/helpers/projectStatus';
import { Placement } from './types';
import { RequestPublicationModal } from 'shared/modals/RequestPublicationModal';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { selectorLMSModuleInfo } from '../../reducers/LMSSlice';
import { CheckboxRowWrapper } from 'shared/pages/project/components/toolsStyles';

const BASIC_DESIGN_TAG_ID = '978saa51s67df76432e70310be98';


export interface ProjectTooltipProps {
  isViewable?: boolean;
  isPublishable?: boolean;
  isDeletable?: boolean;
  isEditable?: boolean;
  canTransfer?: boolean;
  isRemovableFromShowcase?: boolean;
  isRemovableFromBook?: boolean;
  isCanBeDraft?: boolean;
  isCanBeRepost?: boolean;
  isModerately?: boolean;
  identifier?: string;
  isReportable?: boolean;
  isPartInCompetition?: boolean;
  isHiddenCancel?: boolean;
  isCopyLink?: boolean;
  isInOnDisplay?: boolean;
  projectType?: string;
  isSuperModer?: boolean;
  isTeamOwner?: boolean
  isLongreadAndTeamOwner?: boolean
  isTagModerator?: boolean;
  isTagExpert?: boolean;
  orientationType?: ORIENTATION_TYPE,
  isBookmarked?: boolean;
  isCoauthor?: boolean;
  canMakeBilingual?: boolean;
  isPresentation?: boolean;
  headCommentVisible?: boolean;
  canModerSendToCompetition?: boolean;
  canAddBookmark?: boolean;
  competitionList?: Record<string, string[]>[];
  moderId?: string;
  approvalInfo?: {context: string, name: string};
  project?: Project;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  isCanVisibleCompetitionList?: boolean;
  canSendGift?: boolean;
  shouldVisibleProtectButton?: boolean;
  isNominated?: boolean;
  canViewStatistic?: boolean;
  canEditFolders?: boolean;
  isProjectAITranslateRemovable?: boolean;
  toggleTooltip?: (v: any) => void;
  portalNestedDropdowns?: boolean;
  closeOnClickOutsideOnly?: boolean;
  onShouldReload?: (project: Project) => void;
  shouldDisplayShowcases?: boolean;
  isCollectionVote?: boolean;
  placement?: Placement;
  repostTools?: any;
  ref?: any
}

export interface MobileTooltipProps {
  tooltipOpen: boolean,
  toggleTooltip: (value: boolean) => void
}


export const StyledDropdownListItem = styled(DropdownListItem)<{isProfile?: boolean, isContinuumSite?: boolean}>`
  position: relative;
  color: ${({ theme }) => theme.mainDropdownStyles?.color || '#70706F'};
  background: ${({ theme }) => theme.mainDropdownStyles?.bg || '#F2F2F2'};
  box-shadow: 0px 4px 4px 0px #0000001F;

  &:hover {
    color: ${({ theme }) => theme.mainDropdownStyles?.hoverColor || theme.mainDropdownStyles?.color || '#70706F'};
    background: ${({ theme }) => theme.mainDropdownStyles?.hoverBG || '#E7E7E7'};
  }
`;

export const displayProjectEditError = (project: any, toasts: any, t: any, lang: any) => {
  try {
    if (project) {
      const { contexts } = project;
      const hasContexts = !!contexts?.filter((i:string) => i !== 'places' && i !== 'hsedesigntemp')?.length; 
      const contextMap = (project.approvals || []).reduce((result: any, {
        contextService,
        isApproved,
        accountId,
      }: {
        contextService: string,
        isApproved: boolean,
        accountId: string
      }) => {
        return {
          ...result,
          [contextService]: {
            contextService,
            isApproved,
            accountId,
          }
        }
      }, {});
      const approvalsArray = Object.values(contextMap);
      //@ts-ignore
      const [blockingApproval] = approvalsArray.filter(({
        isApproved,
        contextService
      }) => isApproved && contextService !== 'places' && contextService !== 'hsedesigntemp');
      if (blockingApproval) {
        const {
          //@ts-ignore
          accountId,
          //@ts-ignore
          contextService
        } = blockingApproval;
        //@ts-ignore
        DesignerAPI.Get({
          //@ts-ignore
          idDesigner: accountId
        })
        .then((d) => d.json())
        .then((designer) => {
          return API.toLang<Designer>(designer, lang || 'en')
        })
          .then(({ name, surName }) => {
            const approvalInfo = ({
              name: `${name} ${surName}`,
              context: contextService
            });
            toasts.error(t('toasts.project_on_display', approvalInfo));
            return;
          }).catch((e) => { throw e });
      }
      else if (hasContexts) {
        toasts.error(t('toasts.project_on_display_empty'));
        return;
      }
    }
  } catch (e) {
    toasts.error(t('toasts.project_on_display', {}));
  }
}

/**
 * **ProjectTooltip** - контент всплывающего меню со всеми необходимыми параметрами и настройками для проекта.
 *
 * @param props
 */
export const ProjectTooltip: FC<React.PropsWithChildren<ProjectTooltipProps & ProjectEvents>> = (props) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const history = useHistory();
  const profile = useProfile();
  const { error } = useToasts();
  const [showRequestPublicationModal, setShowRequestPublicationModal] = useState(false);
  const lmsModuleInfo = useSelector(selectorLMSModuleInfo);

  const {
    isViewable,
    isPublishable,
    isEditable,
    isReportable,
    isDeletable,
    isCanBeDraft,
    repostTools,
    projectType,
    // isHiddenCancel,
    isCopyLink,
    canTransfer,
    identifier,
    isModerately,
    headCommentVisible,
    isRemovableFromShowcase,
    isRemovableFromBook,
    isTeamOwner,
    isLongreadAndTeamOwner,
    canModerSendToCompetition,
    canAddBookmark,
    isCanBeRepost,
    competitionList,
    isInOnDisplay,
    isPartInCompetition,
    children,
    isCanNotParticipate,
    canEditFolders,
    isPresentation,
    canSendGift,
    toggleTooltip,
    portalNestedDropdowns,
    closeOnClickOutsideOnly,
    onShouldReload,
    shouldDisplayShowcases,
    isCollectionVote,
    onOpenHeadComment,
    onNotParticipate,
    onAddBookmark,
    onRepost,
    onMoveFromShowcase,
    onRemoveFromBook,
    onPublish,
    onDelete,
    onEdit,
    onMoveDrafts,
    onRejectCoauthor,
    onSendToCompetition,
    onMoveModerate,
    onReport,
    onView,
    onProtect,
    onTransfer,
    onExport,
    onMakeImageForOrganization,
    onAttachProducts,
    isTagModerator,
    orientationType,
    isCoauthor,
    isSuperModer,
    project,
    linkPrefix,
    isBookmarked,
    shouldVisibleProtectButton,
    onShowOriginals,
    onDeleteAITranslation,
    canViewStatistic
  } = props;

  const isPro = AppTester.isProDeziiign();
  const isContinuumSite = AppTester.isContinuumSite();
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const isProjectPage = !!useRouteMatch(LinkTo.project);
  const isHSEDesign = AppTester.isHSEDesign();
  const isDeziiign = AppTester.isDeziiign();
  // @ts-ignore
  const isLMS = Boolean(project?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || '')
      || project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item));

  const isModuleProject = Boolean(lmsModuleInfo?.moduleProjectId && lmsModuleInfo?.moduleProjectId === project?.projectId);

  const isOwner = useMemo(() => profile?.accountId === project?.author?.accountId, [profile?.accountId, project?.author?.accountId])

  const isModer = bProfile.hasRole(profile, ProfileRole.moder);
    //@ts-ignore
  const hasRootTeam =  !!project?.rootTeamId;

  const canPrintPDF = hasRootTeam && (isOwner || isModer);
  
  const canEditAllPrj = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && 
      ((new ProfileRole(HsePolicies.edit_project)).containsIn(profile.rolesList) ||
      isLMS && (new ProfileRole(HsePolicies.lms_admin)).containsIn(profile.rolesList))
  ), [isLMS, profile]);

  const canViewOrigImagesPrj = useMemo(
    () => profile && profile.rolesList && Array.isArray(profile.rolesList) &&
       (new ProfileRole(HsePolicies.can_view_originals_project)).containsIn(profile.rolesList)
  , [profile]);

  const isProjectAITranslateRemovable = useMemo(() => {
    //@ts-ignore
    return project?.autoTranslateStatus === 2 || project?.autoTranslateStatus === 3
  }, [project])

  const handleRequestPublication = useCallback(() => {
    if (!project?.projectId) return;
    
  }, [project?.projectId]);

  const handleOpenRequestPublicationModal = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!project?.projectId) {
      return;
    }
    setShowRequestPublicationModal(true);
  }, [project?.projectId]);

  const editFn = onEdit ?? (() => {
    if (!isSuperModer && !isTagModerator && !canEditAllPrj) {

      if (isInOnDisplay && !ProjectDelegate.canEditShowcaseProjects(profile)) {
        displayProjectEditError(project, toasts, t, i18n.language);
        return;
      }
      if (isPartInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
    }

    //@ts-ignore
    if (isPresentation || (['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType) && !isPro && !isLMS)) {
      if (isHSEDesign) {
        if (AppTester.isHseSpace()) {
          const isHseSpaceProPresentationType = ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(
            project?.projectType as string,
          );
          const hseSpaceEditBase = isHseSpaceProPresentationType ? '/pro/edit' : '/edit';
          window.open(`${window.location.origin}${hseSpaceEditBase}/${identifier}`);
        } else {
          const hseProUrl = window.location.origin.includes("com")
            ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM
            : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
          const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
          window.open(`${baseUrl}/edit/${identifier}`);
        }
      }
      else if (isDeziiign && !isHSEDesign) {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      return;
    }
    if (project?.isBook) {
      history.push(((linkPrefix || '') + CreateLinkTo.bookEdit(identifier ?? 'undefined')));
    } else {
      const hasLMSContexts = project?.contexts?.some((ctx: string) => ctx === 'lmshseproject' || ctx === 'lmsproject');
      // @ts-ignore
      const hasLMSModuleDiscipline = !!project?.additionalInfo?.lmsmodule_discipline;
      
      const isModule = hasLMSContexts && 
                       lmsModuleInfo?.moduleProjectId && 
                       lmsModuleInfo.moduleProjectId === project?.projectId;
      const isLikelyModule = !lmsModuleInfo && hasLMSContexts && hasLMSModuleDiscipline;
      
      if (isModule || isLikelyModule) {
        const lmsUrl = window.location.origin.includes("com") 
          ? process.env.REACT_APP_URL_HSEDESIGN_LMS_COM 
          : process.env.REACT_APP_URL_HSEDESIGN_LMS;
        const baseUrl = lmsUrl?.endsWith('/') ? lmsUrl.slice(0, -1) : lmsUrl;
        window.open(`${baseUrl}/edit/${identifier ?? 'undefined'}`);
      } else if (isLMS && ProjectStatusDrafts(project) && hasLMSContexts) {
        window.open(process.env.REACT_APP_URL_HSEDESIGN_LMS_COM + CreateLinkTo.projectEdit(identifier ?? 'undefined'))
      } else {
        history.push(((linkPrefix || '') + CreateLinkTo.projectEdit(identifier ?? 'undefined')));
      }
    }
  });

  const protextButtonText = useMemo(() => {
    const isProcess = (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess;
    const isNotStart = (project as any)?.protectedStatus as number === protectProcessStatuses.NotStarted;

    if (isProcess) {
      return t('inProgress');
    }

    return !(project as any)?.isProtected || isNotStart
      ? t('protect')
      : t('unprotect');
  }, [project, t])

  const printPDFForm = useMemo(() => ({
    orientation: true,
    size: 'A4',
    localization: i18n.language,
    projectId: project?.projectId,
    pathUrlProjectRequest: 'testPp',
    pathUrlLongreadRequest: 'testLn',
    // @ts-ignore
    isPresentation: ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType),
}), [project, i18n.language]);


const printPdfCard = useCallback(() => {

    toasts.info(t('print_project_toast'))

    API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Project/PrintPdf')
        .post()
        .body(printPDFForm)
        .send()
        .then((r) => r.json())
        // @ts-ignore
        .then((r) => r?.length > 0 ? downloadPDF(r, project?.title, error, toasts.info, t) : console.error('no files'))
        .catch((e) => console.error(e))
}, [printPDFForm, t]);


  const protectInprocess = useMemo(
    () => (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess, [project])

  const fireOpen = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation()
    window.open((Endpoints.geograffee + "/api/Project/GetStatisticForProjest?projectId=" + identifier), '_blank')?.focus();
  };

  const fireEvent = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    handler?.(identifier ?? '');
  };

  const fireEventWithoutPreventing = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    handler?.(identifier ?? '');
  };

  const handleGoToEstimate = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (project?.projectId) {
      history.push(`/estimate/${project.projectId}`);
    }
  };

  const isTeacher = useMemo(() => profile && bProfile.isEmployee(profile), [profile]);
  
  const canSuperviseProject = useMemo(
      () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_supervise_project)).containsIn(profile.rolesList)
    ), [profile]);


  const [tp, stp] = useState<any>(project);
  
  const isSelfCurator = !!(profile && tp?.curatorsIdList.includes(profile.accountId));
  const [isSelfCuratorCurrent, setIsSelfCuratorCurrent] = useState(isSelfCurator);

  const isExceptionTag = useMemo(() => (project?.tagsID?.includes(BASIC_DESIGN_TAG_ID)) , [project]);
  

  const canBeCurated = useMemo(() => {
    const { approvals } = project || {};
    const rejectedAtHsedesign = approvals?.some(({ contextService, isApproved }: any) => {
      return ['hsedesign', 'hsedesigntemp'].includes(contextService) && !isApproved;
    })

    return isExceptionTag || project?.contexts?.includes('hsedesign') || rejectedAtHsedesign;
  }, [project, isExceptionTag]);

  const reloadStp = useCallback(() => {
    if (project) {
      ProjectAPI.Get({ idProject: project.projectId })
        .then((v) => v.json())
        .then(stp);
    }
  }, [project]);

  const setSelfCurator = useCallback(async () => {
    if (tp?.curatorsIdList?.length) {
      toasts.error('Куратор уже закреплен за проектом');
      return;
    }

    if (profile && tp && !isSelfCurator) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/SetCurrentUserCuratorProject`)
        .post()
        .body({
          projectId: project?.projectId,
        })
        .send()
        .then((res) => setIsSelfCuratorCurrent(true))

      onShouldReload?.({ ...project, curatorsIdList: [...project?.curatorsIdList, profile?.accountId] });

      reloadStp();

      toasts.info(`Поздравляем! Теперь Вы куратор проекта ${project?.title}`);
    } else {
      toasts.error('Вы уже являетесь куратором данного проекта');
    }
  }, [isSelfCurator, onShouldReload, profile, project, toasts, tp, reloadStp, setIsSelfCuratorCurrent])


  const removeCurators = useCallback(async () => {
    if (profile && tp) {
      await API.fetch(`${Endpoints.geograffee}/api/Project/ClearCurators?projectId=${project?.projectId}`, {})
        .then((res) => setIsSelfCuratorCurrent(false))

      onShouldReload?.({ ...project, curatorsIdList: [...project?.curatorsIdList, profile?.accountId] });

      reloadStp();

      toasts.info(`Список кураторов очищен`);
    } else {
      toasts.error('Не удалось очистить список кураторов');
    }
  }, [onShouldReload, profile, project, toasts, tp, reloadStp]);

  const canClearCuratorsProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_clear_curators_lst_project)).containsIn(profile.rolesList)
  ), [profile]);

  const hasRemoveCuratorsOption = ['f5a705e1e8cb4a80b58582423d51dd0e', '01d7846ac1a34b6f8182646495d1fda6'].includes(profile?.accountId || '') || canClearCuratorsProject;
  
  const handleSelfCuratorChange = useCallback(() => {
    if (!isSelfCurator) {
      if (canBeCurated) {
        setSelfCurator()
      } else {
        toasts.error("Для взятия кураторство необходимо, чтобы проект прошел модерацию супервайзерами");
      }
    } else if (hasRemoveCuratorsOption) {
      removeCurators()
    } else {
      toasts.error(`Для снятия кураторства необходимо обратиться к Ларцеву А.`);
    }
  }, [isSelfCuratorCurrent, isSelfCurator, canBeCurated, toasts, removeCurators, setSelfCurator, hasRemoveCuratorsOption]);

  const isOwnProject = !!project && (project.author?.accountId === (profile?.accountId) || project?.coAuthors?.some((v: any) => v?.accountId === (profile?.accountId)));
  const isStudentProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'student'), [project]);
  const isAlumniProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'hsealumni'), [project]);  
  
  const hasCuratorOptions = (isTeacher || isModer || canSuperviseProject) && (isStudentProject || isAlumniProject) && !isOwnProject && (!tp?.curatorsIdList?.length || tp?.curatorsIdList?.includes(profile?.accountId));

  return (
    <>
        {isViewable && <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onView)}>{t('view').toLowerCase()}</StyledDropdownListItem>}
        {isEditable && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(editFn)}>{t('edit').toLowerCase()}</StyledDropdownListItem>}
        {canTransfer && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(onTransfer)}>{t('transfer_project').toLowerCase()}</StyledDropdownListItem>}
        {canTransfer && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(onExport)}>{t('export_project').toLowerCase()}</StyledDropdownListItem>}
        {!isCollectionVote && project && (
          <CompetitionsDropdown
            project={project}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
            toggleTooltip={toggleTooltip}
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
          />
        )}
        {shouldDisplayShowcases && project && (
          <ShowcasesDropdown
            project={project}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
            toggleTooltip={toggleTooltip}
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
            onShouldReload={onShouldReload}
          />
        )}
        {hasCuratorOptions && isProjectPage && (
          <StyledDropdownListItem>
            <MagicBoxRow
              onClick={handleSelfCuratorChange}
              checked={isSelfCuratorCurrent}
              label="взять кураторство"
            />
          </StyledDropdownListItem>
        )}
        {hasRemoveCuratorsOption && isProjectPage && <StyledDropdownListItem  isContinuumSite={isContinuumSite} isProfile={isProfile}  onClick={removeCurators}>{'снять кураторство'}</StyledDropdownListItem>}
        {isModuleProject && <StyledDropdownListItem isProfile={isProfile} onClick={handleGoToEstimate}>{t('estimate')}</StyledDropdownListItem>}
        {canPrintPDF && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(printPdfCard)}>{t('print_project').toLowerCase()}</StyledDropdownListItem>}
        {isRemovableFromShowcase && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveFromShowcase)}>
            {t('removeFromShowcase').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {canAddBookmark && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAddBookmark)}>{t(isBookmarked ? 'bookmark_remove' : 'bookmark').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeRepost && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRepost)}>{t('repost_project').toLowerCase()}</StyledDropdownListItem>
        )}
        {isPublishable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onPublish)}>{t('publish').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCoauthor && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRejectCoauthor)}>{t('reject_coauthor').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeDraft && !((isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project)) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveDrafts)}>{t('move_drafts').toLowerCase()}</StyledDropdownListItem>
        )}
        {isModerately && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveModerate)}>{t('moderate').toLowerCase()}</StyledDropdownListItem>
        )}
        {isDeletable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDelete)}>{t('delete').toLowerCase()}</StyledDropdownListItem>
        )}
        {isRemovableFromBook && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRemoveFromBook)}>{t('removeFromBook').toLowerCase()}</StyledDropdownListItem>
        )}
        {onDeleteAITranslation && isProjectAITranslateRemovable && (isOwner || isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDeleteAITranslation)}>{t('delete_translation').toLowerCase()}</StyledDropdownListItem>
        )}
        {onShowOriginals && (isSuperModer || canEditAllPrj || canViewOrigImagesPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={onShowOriginals}>{t('show_original').toLowerCase()}</StyledDropdownListItem>
        )}
        {canModerSendToCompetition && !!competitionList?.length && (
          competitionList?.map((competition) => (
            <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={
              () => 
                onSendToCompetition && onSendToCompetition(
                  identifier ?? '', competition?.nominationsID)}>{`Отправить на конкурс${competition?.name}`}</StyledDropdownListItem>
          ))
        )}
        {isReportable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEventWithoutPreventing(onReport)}>{t('send_report').toLowerCase()}</StyledDropdownListItem>
        )}
        {(isTagModerator || canViewStatistic) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireOpen}>{t('moder_log').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanNotParticipate && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onNotParticipate)}>{t('not_participate').toLowerCase()}</StyledDropdownListItem>
        )}
        {shouldVisibleProtectButton && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} disable={protectInprocess} onClick={fireEvent(onProtect)}>{protextButtonText.toLowerCase()}</StyledDropdownListItem>
        )}
        {canSendGift && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(() => alert(`GIFT ID: ${identifier}`))}>{t('gift')}</StyledDropdownListItem>
        )}
        {headCommentVisible && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onOpenHeadComment)}>{t('head_comment')}</StyledDropdownListItem>
        )}
        {onAttachProducts && isLongreadAndTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAttachProducts)}>{t('shop.attach_product')}</StyledDropdownListItem>
        )}
        {isTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMakeImageForOrganization)}>
            <span style={{ lineHeight: '18px', margin: '10px 0 14px 0' }}>{t('make_image_for_teams')}</span>
          </StyledDropdownListItem>
        )}
        {canEditFolders && !ProjectDelegate.isRejectedProject(project) && (
          <FoldersDropdown 
            projectId={project?.projectId || identifier || ''}
            isDraft={ProjectStatusDrafts(project)} 
            toggleTooltip={toggleTooltip} 
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
            orientationType={orientationType}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
          />
        )}
        {(isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project) && (
          <StyledDropdownListItem 
            isContinuumSite={isContinuumSite} 
            isProfile={isProfile} 
            onClick={handleOpenRequestPublicationModal}
          >
            {t('project_edit.request_publication')}
          </StyledDropdownListItem>
        )}
        {children}
        {showRequestPublicationModal && project && (
          createPortal(
            <RequestPublicationModal 
              project={project}
              onConfirm={handleRequestPublication}
              onCancel={() => setShowRequestPublicationModal(false)}
            />,
            document.body
          )
        )}
    </>
  );
};
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectEvents } from '../../helpers/projectEvents';
import { createCopyLinkProjectFn } from '../../helpers/copyLink';
import { useToasts, DropdownListItem, MagicBoxRow } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import styled from 'styled-components';
import { useHistory, useRouteMatch } from "react-router-dom";
import { API, Designer, DesignerAPI, Endpoints, Project, ProjectAPI } from '@brg/api';
import { CompetitionsDropdown } from '../CompetitionsDropdown/CompetitionsDropdown';
import { ShowcasesDropdown } from '../ShowcasesDropdown/ShowcasesDropdown';
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { ProjectDelegate } from "../../../project-delegate";
import { AppTester } from "../../../supporting";
import { protectProcessStatuses } from 'shared/helpers/protectStatus';
import { css } from 'styled-components';
import { HsePolicies } from 'shared/constants/HsePolicies';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import { FoldersDropdown, ORIENTATION_TYPE } from '../FoldersDropdown/FoldersDropdown';
import { downloadPDF } from '../PrintProject/model/downloadPDF';
import { ProjectStatusDrafts } from 'shared/helpers/projectStatus';
import { Placement } from './types';
import { RequestPublicationModal } from 'shared/modals/RequestPublicationModal';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { selectorLMSModuleInfo } from '../../reducers/LMSSlice';
import { CheckboxRowWrapper } from 'shared/pages/project/components/toolsStyles';

const BASIC_DESIGN_TAG_ID = '978saa51s67df76432e70310be98';


export interface ProjectTooltipProps {
  isViewable?: boolean;
  isPublishable?: boolean;
  isDeletable?: boolean;
  isEditable?: boolean;
  canTransfer?: boolean;
  isRemovableFromShowcase?: boolean;
  isRemovableFromBook?: boolean;
  isCanBeDraft?: boolean;
  isCanBeRepost?: boolean;
  isModerately?: boolean;
  identifier?: string;
  isReportable?: boolean;
  isPartInCompetition?: boolean;
  isHiddenCancel?: boolean;
  isCopyLink?: boolean;
  isInOnDisplay?: boolean;
  projectType?: string;
  isSuperModer?: boolean;
  isTeamOwner?: boolean
  isLongreadAndTeamOwner?: boolean
  isTagModerator?: boolean;
  isTagExpert?: boolean;
  orientationType?: ORIENTATION_TYPE,
  isBookmarked?: boolean;
  isCoauthor?: boolean;
  canMakeBilingual?: boolean;
  isPresentation?: boolean;
  headCommentVisible?: boolean;
  canModerSendToCompetition?: boolean;
  canAddBookmark?: boolean;
  competitionList?: Record<string, string[]>[];
  moderId?: string;
  approvalInfo?: {context: string, name: string};
  project?: Project;
  linkPrefix?: string;
  isCanNotParticipate?: boolean;
  isCanVisibleCompetitionList?: boolean;
  canSendGift?: boolean;
  shouldVisibleProtectButton?: boolean;
  isNominated?: boolean;
  canViewStatistic?: boolean;
  canEditFolders?: boolean;
  isProjectAITranslateRemovable?: boolean;
  toggleTooltip?: (v: any) => void;
  portalNestedDropdowns?: boolean;
  closeOnClickOutsideOnly?: boolean;
  onShouldReload?: (project: Project) => void;
  shouldDisplayShowcases?: boolean;
  isCollectionVote?: boolean;
  placement?: Placement;
  repostTools?: any;
  ref?: any
}

export interface MobileTooltipProps {
  tooltipOpen: boolean,
  toggleTooltip: (value: boolean) => void
}


export const StyledDropdownListItem = styled(DropdownListItem)<{isProfile?: boolean, isContinuumSite?: boolean}>`
  position: relative;
  color: ${({ theme }) => theme.mainDropdownStyles?.color || '#70706F'};
  background: ${({ theme }) => theme.mainDropdownStyles?.bg || '#F2F2F2'};
  box-shadow: 0px 4px 4px 0px #0000001F;

  &:hover {
    color: ${({ theme }) => theme.mainDropdownStyles?.hoverColor || theme.mainDropdownStyles?.color || '#70706F'};
    background: ${({ theme }) => theme.mainDropdownStyles?.hoverBG || '#E7E7E7'};
  }
`;

export const displayProjectEditError = (project: any, toasts: any, t: any, lang: any) => {
  try {
    if (project) {
      const { contexts } = project;
      const hasContexts = !!contexts?.filter((i:string) => i !== 'places' && i !== 'hsedesigntemp')?.length; 
      const contextMap = (project.approvals || []).reduce((result: any, {
        contextService,
        isApproved,
        accountId,
      }: {
        contextService: string,
        isApproved: boolean,
        accountId: string
      }) => {
        return {
          ...result,
          [contextService]: {
            contextService,
            isApproved,
            accountId,
          }
        }
      }, {});
      const approvalsArray = Object.values(contextMap);
      //@ts-ignore
      const [blockingApproval] = approvalsArray.filter(({
        isApproved,
        contextService
      }) => isApproved && contextService !== 'places' && contextService !== 'hsedesigntemp');
      if (blockingApproval) {
        const {
          //@ts-ignore
          accountId,
          //@ts-ignore
          contextService
        } = blockingApproval;
        //@ts-ignore
        DesignerAPI.Get({
          //@ts-ignore
          idDesigner: accountId
        })
        .then((d) => d.json())
        .then((designer) => {
          return API.toLang<Designer>(designer, lang || 'en')
        })
          .then(({ name, surName }) => {
            const approvalInfo = ({
              name: `${name} ${surName}`,
              context: contextService
            });
            toasts.error(t('toasts.project_on_display', approvalInfo));
            return;
          }).catch((e) => { throw e });
      }
      else if (hasContexts) {
        toasts.error(t('toasts.project_on_display_empty'));
        return;
      }
    }
  } catch (e) {
    toasts.error(t('toasts.project_on_display', {}));
  }
}

/**
 * **ProjectTooltip** - контент всплывающего меню со всеми необходимыми параметрами и настройками для проекта.
 *
 * @param props
 */
export const ProjectTooltip: FC<React.PropsWithChildren<ProjectTooltipProps & ProjectEvents>> = (props) => {
  const { t, i18n } = useTranslation();
  const toasts = useToasts();
  const history = useHistory();
  const profile = useProfile();
  const { error } = useToasts();
  const [showRequestPublicationModal, setShowRequestPublicationModal] = useState(false);
  const lmsModuleInfo = useSelector(selectorLMSModuleInfo);

  const {
    isViewable,
    isPublishable,
    isEditable,
    isReportable,
    isDeletable,
    isCanBeDraft,
    repostTools,
    projectType,
    // isHiddenCancel,
    isCopyLink,
    canTransfer,
    identifier,
    isModerately,
    headCommentVisible,
    isRemovableFromShowcase,
    isRemovableFromBook,
    isTeamOwner,
    isLongreadAndTeamOwner,
    canModerSendToCompetition,
    canAddBookmark,
    isCanBeRepost,
    competitionList,
    isInOnDisplay,
    isPartInCompetition,
    children,
    isCanNotParticipate,
    canEditFolders,
    isPresentation,
    canSendGift,
    toggleTooltip,
    portalNestedDropdowns,
    closeOnClickOutsideOnly,
    onShouldReload,
    shouldDisplayShowcases,
    isCollectionVote,
    onOpenHeadComment,
    onNotParticipate,
    onAddBookmark,
    onRepost,
    onMoveFromShowcase,
    onRemoveFromBook,
    onPublish,
    onDelete,
    onEdit,
    onMoveDrafts,
    onRejectCoauthor,
    onSendToCompetition,
    onMoveModerate,
    onReport,
    onView,
    onProtect,
    onTransfer,
    onExport,
    onMakeImageForOrganization,
    onAttachProducts,
    isTagModerator,
    orientationType,
    isCoauthor,
    isSuperModer,
    project,
    linkPrefix,
    isBookmarked,
    shouldVisibleProtectButton,
    onShowOriginals,
    onDeleteAITranslation,
    canViewStatistic
  } = props;

  const isPro = AppTester.isProDeziiign();
  const isContinuumSite = AppTester.isContinuumSite();
  const isProfile = !!useRouteMatch(LinkTo.designer);
  const isProjectPage = !!useRouteMatch(LinkTo.project);
  const isHSEDesign = AppTester.isHSEDesign();
  const isDeziiign = AppTester.isDeziiign();
  // @ts-ignore
  const isLMS = Boolean(project?.teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || '')
      || project?.contexts?.some(( item: string ) => ['lmsproject', 'lmshseproject'].includes(item));

  const isModuleProject = Boolean(lmsModuleInfo?.moduleProjectId && lmsModuleInfo?.moduleProjectId === project?.projectId);

  const isOwner = useMemo(() => profile?.accountId === project?.author?.accountId, [profile?.accountId, project?.author?.accountId])

  const isModer = bProfile.hasRole(profile, ProfileRole.moder);
    //@ts-ignore
  const hasRootTeam =  !!project?.rootTeamId;

  const canPrintPDF = hasRootTeam && (isOwner || isModer);
  
  const canEditAllPrj = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && 
      ((new ProfileRole(HsePolicies.edit_project)).containsIn(profile.rolesList) ||
      isLMS && (new ProfileRole(HsePolicies.lms_admin)).containsIn(profile.rolesList))
  ), [isLMS, profile]);

  const canViewOrigImagesPrj = useMemo(
    () => profile && profile.rolesList && Array.isArray(profile.rolesList) &&
       (new ProfileRole(HsePolicies.can_view_originals_project)).containsIn(profile.rolesList)
  , [profile]);

  const isProjectAITranslateRemovable = useMemo(() => {
    //@ts-ignore
    return project?.autoTranslateStatus === 2 || project?.autoTranslateStatus === 3
  }, [project])

  const handleRequestPublication = useCallback(() => {
    if (!project?.projectId) return;
    
  }, [project?.projectId]);

  const handleOpenRequestPublicationModal = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!project?.projectId) {
      return;
    }
    setShowRequestPublicationModal(true);
  }, [project?.projectId]);

  const editFn = onEdit ?? (() => {
    if (!isSuperModer && !isTagModerator && !canEditAllPrj) {

      if (isInOnDisplay && !ProjectDelegate.canEditShowcaseProjects(profile)) {
        displayProjectEditError(project, toasts, t, i18n.language);
        return;
      }
      if (isPartInCompetition) {
        toasts.error(t('toasts.project_part_competition'));
        return;
      }
    }

    //@ts-ignore
    if (isPresentation || (['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType) && !isPro && !isLMS)) {
      if (isHSEDesign) {
        if (AppTester.isHseSpace()) {
          const isHseSpaceProPresentationType = ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(
            project?.projectType as string,
          );
          const hseSpaceEditBase = isHseSpaceProPresentationType ? '/pro/edit' : '/edit';
          window.open(`${window.location.origin}${hseSpaceEditBase}/${identifier}`);
        } else {
          const hseProUrl = window.location.origin.includes("com")
            ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM
            : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
          const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
          window.open(`${baseUrl}/edit/${identifier}`);
        }
      }
      else if (isDeziiign && !isHSEDesign) {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      else {
        const baseUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO?.endsWith('/') 
          ? process.env.REACT_APP_URL_DEZIIIGN_PRO.slice(0, -1) 
          : process.env.REACT_APP_URL_DEZIIIGN_PRO;
        window.open(`${baseUrl}/edit/${identifier}`)
      }
      return;
    }
    if (project?.isBook) {
      history.push(((linkPrefix || '') + CreateLinkTo.bookEdit(identifier ?? 'undefined')));
    } else {
      const hasLMSContexts = project?.contexts?.some((ctx: string) => ctx === 'lmshseproject' || ctx === 'lmsproject');
      // @ts-ignore
      const hasLMSModuleDiscipline = !!project?.additionalInfo?.lmsmodule_discipline;
      
      const isModule = hasLMSContexts && 
                       lmsModuleInfo?.moduleProjectId && 
                       lmsModuleInfo.moduleProjectId === project?.projectId;
      const isLikelyModule = !lmsModuleInfo && hasLMSContexts && hasLMSModuleDiscipline;
      
      if (isModule || isLikelyModule) {
        const lmsUrl = window.location.origin.includes("com") 
          ? process.env.REACT_APP_URL_HSEDESIGN_LMS_COM 
          : process.env.REACT_APP_URL_HSEDESIGN_LMS;
        const baseUrl = lmsUrl?.endsWith('/') ? lmsUrl.slice(0, -1) : lmsUrl;
        window.open(`${baseUrl}/edit/${identifier ?? 'undefined'}`);
      } else if (isLMS && ProjectStatusDrafts(project) && hasLMSContexts) {
        window.open(process.env.REACT_APP_URL_HSEDESIGN_LMS_COM + CreateLinkTo.projectEdit(identifier ?? 'undefined'))
      } else {
        history.push(((linkPrefix || '') + CreateLinkTo.projectEdit(identifier ?? 'undefined')));
      }
    }
  });

  const protextButtonText = useMemo(() => {
    const isProcess = (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess;
    const isNotStart = (project as any)?.protectedStatus as number === protectProcessStatuses.NotStarted;

    if (isProcess) {
      return t('inProgress');
    }

    return !(project as any)?.isProtected || isNotStart
      ? t('protect')
      : t('unprotect');
  }, [project, t])

  const printPDFForm = useMemo(() => ({
    orientation: true,
    size: 'A4',
    localization: i18n.language,
    projectId: project?.projectId,
    pathUrlProjectRequest: 'testPp',
    pathUrlLongreadRequest: 'testLn',
    // @ts-ignore
    isPresentation: ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(project?.projectType),
}), [project, i18n.language]);


const printPdfCard = useCallback(() => {

    toasts.info(t('print_project_toast'))

    API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Project/PrintPdf')
        .post()
        .body(printPDFForm)
        .send()
        .then((r) => r.json())
        // @ts-ignore
        .then((r) => r?.length > 0 ? downloadPDF(r, project?.title, error, toasts.info, t) : console.error('no files'))
        .catch((e) => console.error(e))
}, [printPDFForm, t]);


  const protectInprocess = useMemo(
    () => (project as any)?.protectedStatus as number === protectProcessStatuses.InProcess, [project])

  const fireOpen = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation()
    window.open((Endpoints.geograffee + "/api/Project/GetStatisticForProjest?projectId=" + identifier), '_blank')?.focus();
  };

  const fireEvent = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    handler?.(identifier ?? '');
  };

  const fireEventWithoutPreventing = (handler?: (s: string) => void) => (ev: React.MouseEvent) => {
    handler?.(identifier ?? '');
  };

  const handleGoToEstimate = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (project?.projectId) {
      history.push(`/estimate/${project.projectId}`);
    }
  };

  const isTeacher = useMemo(() => profile && bProfile.isEmployee(profile), [profile]);
  
  const canSuperviseProject = useMemo(
      () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_supervise_project)).containsIn(profile.rolesList)
    ), [profile]);


  const [tp, stp] = useState<any>(project);
  
  const isSelfCurator = !!(profile && tp?.curatorsIdList.includes(profile.accountId));
  const [isSelfCuratorCurrent, setIsSelfCuratorCurrent] = useState(isSelfCurator);

  const isExceptionTag = useMemo(() => (project?.tagsID?.includes(BASIC_DESIGN_TAG_ID)) , [project]);
  

  const canBeCurated = useMemo(() => {
    const { approvals } = project || {};
    const rejectedAtHsedesign = approvals?.some(({ contextService, isApproved }: any) => {
      return ['hsedesign', 'hsedesigntemp'].includes(contextService) && !isApproved;
    })

    return isExceptionTag || project?.contexts?.includes('hsedesign') || rejectedAtHsedesign;
  }, [project, isExceptionTag]);

  const reloadStp = useCallback(() => {
    if (project) {
      ProjectAPI.Get({ idProject: project.projectId })
        .then((v) => v.json())
        .then(stp);
    }
  }, [project]);

  const setSelfCurator = useCallback(async () => {
    if (tp?.curatorsIdList?.length) {
      toasts.error('Куратор уже закреплен за проектом');
      return;
    }

    if (profile && tp && !isSelfCurator) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/SetCurrentUserCuratorProject`)
        .post()
        .body({
          projectId: project?.projectId,
        })
        .send()
        .then((res) => setIsSelfCuratorCurrent(true))

      onShouldReload?.({ ...project, curatorsIdList: [...project?.curatorsIdList, profile?.accountId] });

      reloadStp();

      toasts.info(`Поздравляем! Теперь Вы куратор проекта ${project?.title}`);
    } else {
      toasts.error('Вы уже являетесь куратором данного проекта');
    }
  }, [isSelfCurator, onShouldReload, profile, project, toasts, tp, reloadStp, setIsSelfCuratorCurrent])


  const removeCurators = useCallback(async () => {
    if (profile && tp) {
      await API.fetch(`${Endpoints.geograffee}/api/Project/ClearCurators?projectId=${project?.projectId}`, {})
        .then((res) => setIsSelfCuratorCurrent(false))

      onShouldReload?.({ ...project, curatorsIdList: [...project?.curatorsIdList, profile?.accountId] });

      reloadStp();

      toasts.info(`Список кураторов очищен`);
    } else {
      toasts.error('Не удалось очистить список кураторов');
    }
  }, [onShouldReload, profile, project, toasts, tp, reloadStp]);

  const canClearCuratorsProject = useMemo(
    () => (profile && profile.rolesList && Array.isArray(profile.rolesList) && (new ProfileRole(HsePolicies.can_clear_curators_lst_project)).containsIn(profile.rolesList)
  ), [profile]);

  const hasRemoveCuratorsOption = ['f5a705e1e8cb4a80b58582423d51dd0e', '01d7846ac1a34b6f8182646495d1fda6'].includes(profile?.accountId || '') || canClearCuratorsProject;
  
  const handleSelfCuratorChange = useCallback(() => {
    if (!isSelfCurator) {
      if (canBeCurated) {
        setSelfCurator()
      } else {
        toasts.error("Для взятия кураторство необходимо, чтобы проект прошел модерацию супервайзерами");
      }
    } else if (hasRemoveCuratorsOption) {
      removeCurators()
    } else {
      toasts.error(`Для снятия кураторства необходимо обратиться к Ларцеву А.`);
    }
  }, [isSelfCuratorCurrent, isSelfCurator, canBeCurated, toasts, removeCurators, setSelfCurator, hasRemoveCuratorsOption]);

  const isOwnProject = !!project && (project.author?.accountId === (profile?.accountId) || project?.coAuthors?.some((v: any) => v?.accountId === (profile?.accountId)));
  const isStudentProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'student'), [project]);
  const isAlumniProject = useMemo(() => project?.authorGlobalTypes?.some((type: string) => type?.toLowerCase() === 'hsealumni'), [project]);  
  
  const hasCuratorOptions = (isTeacher || isModer || canSuperviseProject) && (isStudentProject || isAlumniProject) && !isOwnProject && (!tp?.curatorsIdList?.length || tp?.curatorsIdList?.includes(profile?.accountId));

  return (
    <>
        {isViewable && <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onView)}>{t('view').toLowerCase()}</StyledDropdownListItem>}
        {isEditable && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(editFn)}>{t('edit').toLowerCase()}</StyledDropdownListItem>}
        {canTransfer && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(onTransfer)}>{t('transfer_project').toLowerCase()}</StyledDropdownListItem>}
        {canTransfer && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(onExport)}>{t('export_project').toLowerCase()}</StyledDropdownListItem>}
        {!isCollectionVote && project && (
          <CompetitionsDropdown
            project={project}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
            toggleTooltip={toggleTooltip}
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
          />
        )}
        {shouldDisplayShowcases && project && (
          <ShowcasesDropdown
            project={project}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
            toggleTooltip={toggleTooltip}
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
            onShouldReload={onShouldReload}
          />
        )}
        {hasCuratorOptions && isProjectPage && (
          <StyledDropdownListItem>
            <MagicBoxRow
              onClick={handleSelfCuratorChange}
              checked={isSelfCuratorCurrent}
              label="взять кураторство"
            />
          </StyledDropdownListItem>
        )}
        {hasRemoveCuratorsOption && isProjectPage && <StyledDropdownListItem  isContinuumSite={isContinuumSite} isProfile={isProfile}  onClick={removeCurators}>{'снять кураторство'}</StyledDropdownListItem>}
        {isModuleProject && <StyledDropdownListItem isProfile={isProfile} onClick={handleGoToEstimate}>{t('estimate')}</StyledDropdownListItem>}
        {canPrintPDF && <StyledDropdownListItem  isContinuumSite={isContinuumSite}isProfile={isProfile}  onClick={fireEvent(printPdfCard)}>{t('print_project').toLowerCase()}</StyledDropdownListItem>}
        {isRemovableFromShowcase && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveFromShowcase)}>
            {t('removeFromShowcase').toLowerCase()}
          </StyledDropdownListItem>
        )}
        {canAddBookmark && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAddBookmark)}>{t(isBookmarked ? 'bookmark_remove' : 'bookmark').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeRepost && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRepost)}>{t('repost_project').toLowerCase()}</StyledDropdownListItem>
        )}
        {isPublishable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onPublish)}>{t('publish').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCoauthor && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRejectCoauthor)}>{t('reject_coauthor').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanBeDraft && !((isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project)) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveDrafts)}>{t('move_drafts').toLowerCase()}</StyledDropdownListItem>
        )}
        {isModerately && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMoveModerate)}>{t('moderate').toLowerCase()}</StyledDropdownListItem>
        )}
        {isDeletable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDelete)}>{t('delete').toLowerCase()}</StyledDropdownListItem>
        )}
        {isRemovableFromBook && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onRemoveFromBook)}>{t('removeFromBook').toLowerCase()}</StyledDropdownListItem>
        )}
        {onDeleteAITranslation && isProjectAITranslateRemovable && (isOwner || isSuperModer || canEditAllPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onDeleteAITranslation)}>{t('delete_translation').toLowerCase()}</StyledDropdownListItem>
        )}
        {onShowOriginals && (isSuperModer || canEditAllPrj || canViewOrigImagesPrj) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={onShowOriginals}>{t('show_original').toLowerCase()}</StyledDropdownListItem>
        )}
        {canModerSendToCompetition && !!competitionList?.length && (
          competitionList?.map((competition) => (
            <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={
              () => 
                onSendToCompetition && onSendToCompetition(
                  identifier ?? '', competition?.nominationsID)}>{`Отправить на конкурс${competition?.name}`}</StyledDropdownListItem>
          ))
        )}
        {isReportable && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEventWithoutPreventing(onReport)}>{t('send_report').toLowerCase()}</StyledDropdownListItem>
        )}
        {(isTagModerator || canViewStatistic) && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireOpen}>{t('moder_log').toLowerCase()}</StyledDropdownListItem>
        )}
        {isCanNotParticipate && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onNotParticipate)}>{t('not_participate').toLowerCase()}</StyledDropdownListItem>
        )}
        {shouldVisibleProtectButton && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} disable={protectInprocess} onClick={fireEvent(onProtect)}>{protextButtonText.toLowerCase()}</StyledDropdownListItem>
        )}
        {canSendGift && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(() => alert(`GIFT ID: ${identifier}`))}>{t('gift')}</StyledDropdownListItem>
        )}
        {headCommentVisible && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onOpenHeadComment)}>{t('head_comment')}</StyledDropdownListItem>
        )}
        {onAttachProducts && isLongreadAndTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onAttachProducts)}>{t('shop.attach_product')}</StyledDropdownListItem>
        )}
        {isTeamOwner && (
          <StyledDropdownListItem isContinuumSite={isContinuumSite} isProfile={isProfile} onClick={fireEvent(onMakeImageForOrganization)}>
            <span style={{ lineHeight: '18px', margin: '10px 0 14px 0' }}>{t('make_image_for_teams')}</span>
          </StyledDropdownListItem>
        )}
        {canEditFolders && !ProjectDelegate.isRejectedProject(project) && (
          <FoldersDropdown 
            projectId={project?.projectId || identifier || ''}
            isDraft={ProjectStatusDrafts(project)} 
            toggleTooltip={toggleTooltip} 
            portal={portalNestedDropdowns}
            closeOnMouseLeave={closeOnClickOutsideOnly ? false : undefined}
            orientationType={orientationType}
            isContinuumSite={isContinuumSite}
            isProfile={isProfile}
          />
        )}
        {(isOwner || isCoauthor) && project && ProjectDelegate.isRejectedProject(project) && (
          <StyledDropdownListItem 
            isContinuumSite={isContinuumSite} 
            isProfile={isProfile} 
            onClick={handleOpenRequestPublicationModal}
          >
            {t('project_edit.request_publication')}
          </StyledDropdownListItem>
        )}
        {children}
        {showRequestPublicationModal && project && (
          createPortal(
            <RequestPublicationModal 
              project={project}
              onConfirm={handleRequestPublication}
              onCancel={() => setShowRequestPublicationModal(false)}
            />,
            document.body
          )
        )}
    </>
  );
};
